import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Eye, EyeOff, Check, X, Zap, ArrowLeft, Play, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import PageTransition from "@/components/PageTransition";
import QuantumBackground from "@/components/QuantumBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

function PasswordStrength({ password }) {
  const checks = [
    { label: "12+ characters", ok: password.length >= 12 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Lowercase", ok: /[a-z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Special char", ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) },
  ];

  if (!password) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {checks.map((c) => (
        <span key={c.label} className={`flex items-center gap-1 text-xs ${c.ok ? "text-emerald-400" : "text-[var(--qn-foreground-muted)]"}`}>
          {c.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {c.label}
        </span>
      ))}
    </div>
  );
}

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const [mode, setMode] = useState("login"); // login | register | forgot | mfa
  const [loading, setLoading] = useState(false);
  const [dgDemoAvailable, setDgDemoAvailable] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/demo-login/status`).then((r) => setDgDemoAvailable(!!r.data?.enabled)).catch(() => setDgDemoAvailable(false));
  }, []);

  // One-click stage logins (enabled server-side via DEMO_LOGIN_ENABLED). Credentials never
  // leave backend .env; the same cookies as a normal login are issued.
  const quickLogin = async (path, okMsg, dest) => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}${path}`, {}, { withCredentials: true });
      toast.success(okMsg);
      navigate(dest);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  const handleDemoUserLogin = () => quickLogin("/auth/demo-login", "Signed in as demo user — opening the Workspace Dashboard", "/app");
  const handlePlatformAdminLogin = () => quickLogin("/auth/demo-login/platform-admin", "Signed in as platform admin", "/app/platform-admin");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // MFA fields
  const [mfaUserId, setMfaUserId] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  // Register fields
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [accountType, setAccountType] = useState("individual");
  const [teamSize, setTeamSize] = useState("");
  const [complianceRequired, setComplianceRequired] = useState(false);

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const returnTo = searchParams.get("return_to") || "/app";

  const handleLogin = async (e) => {
    e.preventDefault();
    await doLogin(email, password);
  };

  const doLogin = async (loginEmail, loginPassword) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword }, { withCredentials: true });
      if (res.data?.mfa_required) {
        setMfaUserId(res.data.user_id);
        setMfaCode("");
        setMode("mfa");
        toast.info("Enter your authenticator code");
      } else {
        toast.success("Welcome back!");
        navigate(returnTo);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/mfa/verify-login`, { user_id: mfaUserId, code: mfaCode }, { withCredentials: true });
      toast.success("Welcome back!");
      navigate(returnTo);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/register`, {
        email, password, full_name: fullName, company,
        account_type: accountType, team_size: teamSize,
        compliance_required: complianceRequired,
      }, { withCredentials: true });
      toast.success("Account created! Welcome to QNNEAL.");
      navigate("/onboarding");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Check your email for reset instructions");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const cardBg = isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.9)";
  const textMuted = isDark ? "text-white/50" : "text-gray-500";

  return (
    <PageTransition>
      <div className={`min-h-screen relative flex items-center justify-center p-6 pb-32 ${isDark ? "bg-slate-50 dark:bg-[#0A0A1A]" : "bg-[#F8F9FC]"}`}>
        <div className="fixed inset-0 z-0"><QuantumBackground /></div>
        <div className="fixed inset-0 z-[1] pointer-events-none" style={{ background: isDark ? "radial-gradient(ellipse 70% 70% at 50% 45%, transparent 0%, rgba(5,5,15,0.7) 100%)" : "radial-gradient(ellipse 70% 70% at 50% 45%, transparent 0%, rgba(248,249,252,0.5) 100%)" }} />

        <Button variant="ghost" className={`absolute top-6 left-6 z-20 ${textMuted}`} onClick={() => navigate("/")} data-testid="back-to-landing-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
        </Button>

        <motion.div className="relative z-10 w-full max-w-md max-h-[85vh] overflow-y-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-white/10" style={{ background: cardBg, backdropFilter: "blur(16px)" }}>
            <CardHeader className="text-center space-y-3 pb-4">
              <img src={LOGO_ICON} alt="QNNEAL" className="h-16 w-16 mx-auto" />
              <CardTitle className="text-xl font-bold">
                {mode === "login" && "Welcome Back to QNNEAL"}
                {mode === "register" && "Create Your QNNEAL Account"}
                {mode === "forgot" && "Reset Your Password"}
                {mode === "mfa" && "Two-Factor Authentication"}
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg" data-testid="auth-error">
                  {error}
                </div>
              )}

              {/* === LOGIN FORM === */}
              {mode === "login" && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className={`text-sm ${textMuted}`}>Email</label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 bg-white/5 border-white/10" data-testid="login-email" required autoFocus />
                  </div>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Password</label>
                    <div className="relative mt-1">
                      <Input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="pr-10 bg-white/5 border-white/10" data-testid="login-password" required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90 py-5" data-testid="login-submit">
                    {loading ? "Logging in..." : "Log In"}
                  </Button>
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className={`text-sm ${textMuted} hover:text-[var(--qn-primary)] transition-colors w-full text-center`} data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </form>
              )}

              {/* === MFA FORM === */}
              {mode === "mfa" && (
                <form onSubmit={handleMfaVerify} className="space-y-4">
                  <p className={`text-sm ${textMuted} text-center`}>Enter the 6-digit code from your authenticator app, or use a backup code.</p>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Authentication Code</label>
                    <Input value={mfaCode} onChange={e => setMfaCode(e.target.value)} placeholder="000000" className="mt-1 bg-white/5 border-white/10 text-center text-2xl tracking-[0.5em] font-mono" data-testid="mfa-code-input" required autoFocus maxLength={8} autoComplete="one-time-code" />
                  </div>
                  <Button type="submit" disabled={loading || !mfaCode} className="w-full bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90 py-5" data-testid="mfa-submit">
                    {loading ? "Verifying..." : "Verify"}
                  </Button>
                  <button type="button" onClick={() => { setMode("login"); setError(""); setMfaCode(""); }} className={`text-sm ${textMuted} hover:text-[var(--qn-primary)] transition-colors w-full text-center`} data-testid="mfa-back-btn">
                    Back to login
                  </button>
                </form>
              )}

              {/* === REGISTER FORM === */}
              {mode === "register" && (
                <form onSubmit={handleRegister} className="space-y-3">
                  {/* Account Type Toggle */}
                  <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="account-type-toggle">
                    <button type="button" onClick={() => setAccountType("individual")} className={`flex-1 py-2 text-sm font-medium transition-all ${accountType === "individual" ? "bg-[var(--qn-primary)] text-white" : "bg-white/5 text-white/50 hover:text-white/70"}`} data-testid="account-type-individual">
                      Individual
                    </button>
                    <button type="button" onClick={() => setAccountType("enterprise")} className={`flex-1 py-2 text-sm font-medium transition-all ${accountType === "enterprise" ? "bg-[var(--qn-primary)] text-white" : "bg-white/5 text-white/50 hover:text-white/70"}`} data-testid="account-type-enterprise">
                      Team / Enterprise
                    </button>
                  </div>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Full Name</label>
                    <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Smith" className="mt-1 bg-white/5 border-white/10" data-testid="register-name" required autoFocus />
                  </div>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Work Email</label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@company.com" className="mt-1 bg-white/5 border-white/10" data-testid="register-email" required />
                  </div>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Password</label>
                    <div className="relative mt-1">
                      <Input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" className="pr-10 bg-white/5 border-white/10" data-testid="register-password" required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <PasswordStrength password={password} />
                  </div>
                  <div>
                    <label className={`text-sm ${textMuted}`}>Company {accountType === "individual" && <span className="text-white/30">(optional)</span>}</label>
                    <Input value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Corp" className="mt-1 bg-white/5 border-white/10" data-testid="register-company" required={accountType === "enterprise"} />
                  </div>
                  {/* Enterprise-only fields */}
                  {accountType === "enterprise" && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 fade-in duration-200">
                      <div>
                        <label className={`text-sm ${textMuted}`}>Team Size</label>
                        <select value={teamSize} onChange={e => setTeamSize(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="register-team-size">
                          <option value="">Select team size</option>
                          <option value="2-10">2-10</option>
                          <option value="11-50">11-50</option>
                          <option value="51-200">51-200</option>
                          <option value="200+">200+</option>
                        </select>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer" data-testid="register-compliance-checkbox">
                        <input type="checkbox" checked={complianceRequired} onChange={e => setComplianceRequired(e.target.checked)} className="rounded border-white/30 bg-white/5 text-[var(--qn-primary)]" />
                        <span className={`text-sm ${textMuted}`}>We require SOC 2 / GDPR compliance</span>
                      </label>
                    </div>
                  )}
                  <Button type="submit" disabled={loading} className="w-full bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90 py-5" data-testid="register-submit">
                    {loading ? "Creating account..." : accountType === "enterprise" ? "Create Team Account" : "Create Account"}
                  </Button>
                </form>
              )}

              {/* === FORGOT PASSWORD === */}
              {mode === "forgot" && !forgotSent && (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className={`text-sm ${textMuted}`}>Enter your email and we'll send you a link to reset your password.</p>
                  <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" className="bg-white/5 border-white/10" data-testid="forgot-email" required autoFocus />
                  <Button type="submit" disabled={loading} className="w-full bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90 py-5" data-testid="forgot-submit">
                    {loading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <button type="button" onClick={() => { setMode("login"); setError(""); }} className={`text-sm ${textMuted} hover:text-[var(--qn-primary)] w-full text-center`}>
                    Back to login
                  </button>
                </form>
              )}
              {mode === "forgot" && forgotSent && (
                <div className="text-center space-y-3">
                  <div className="text-emerald-400 text-sm">Check your email for a password reset link.</div>
                  <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} className={`text-sm ${textMuted} hover:text-[var(--qn-primary)]`}>
                    Back to login
                  </button>
                </div>
              )}

              {/* Divider + OAuth + Demo */}
              {mode !== "forgot" && mode !== "mfa" && (
                <>
                  <div className="relative my-4">
                    <Separator className="bg-white/10" />
                    <span className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs uppercase ${textMuted}`} style={{ background: isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.9)" }}>
                      or continue with
                    </span>
                  </div>

                  <Button variant="outline" onClick={handleGoogleLogin} className="w-full py-5 border-white/10 hover:bg-white/5" data-testid="google-login-btn">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>

                  {process.env.REACT_APP_GITHUB_CLIENT_ID && (
                    <Button variant="outline" onClick={() => { window.location.href = `https://github.com/login/oauth/authorize?client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&scope=read:user user:email&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/github/callback')}`; }} className="w-full py-5 border-white/10 hover:bg-white/5" data-testid="github-login-btn">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      Continue with GitHub
                    </Button>
                  )}

                  <div className="relative my-3">
                    <Separator className="bg-white/10" />
                    <span className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs uppercase ${textMuted}`} style={{ background: isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.9)" }}>
                      or quick login
                    </span>
                  </div>

                  {dgDemoAvailable && (
                    <div className="space-y-2" data-testid="quick-login-block">
                      <Button onClick={handleDemoUserLogin} disabled={loading} className="w-full py-5 bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90 text-white font-semibold" data-testid="demo-user-login-btn">
                        <Play className="w-4 h-4 mr-2" />
                        Log in as user — Workspace Dashboard
                      </Button>
                      <Button variant="outline" onClick={handlePlatformAdminLogin} disabled={loading} className="w-full py-5 border-white/15 hover:bg-white/5 font-semibold" data-testid="demo-platform-admin-login-btn">
                        <Shield className="w-4 h-4 mr-2" />
                        Log in as platform admin
                      </Button>
                      <p className={`text-[11px] text-center ${textMuted}`}>
                        Stage accounts seeded on the server — real Runs, real solver work, Leap gate closed.
                      </p>
                    </div>
                  )}

                  <p className={`text-center text-sm pt-2 ${textMuted}`}>
                    {mode === "login" ? (
                      <>Don't have an account? <button type="button" onClick={() => { setMode("register"); setError(""); }} className="text-[var(--qn-primary)] hover:underline" data-testid="switch-to-register">Get Started Free</button></>
                    ) : (
                      <>Already have an account? <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-[var(--qn-primary)] hover:underline" data-testid="switch-to-login">Log in</button></>
                    )}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageTransition>
  );
}
